import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, UntypedFormGroup } from '@angular/forms';
import { enumEntityModelCode } from '@app/data-layer/enums/entities';
import { enumEntityType } from '@app/data-layer/enums/isf';
import { AddressModel, CountryModel, ShipmentModel } from '@app/data-layer/models/common';
import { EntityModel } from '@app/data-layer/models/entities';
import { InvoiceModel, LineModel } from '@app/data-layer/models/entry-summaries';
import { IsfModel } from '@app/data-layer/models/isf';
import { IsfV1EntityHtsModel } from '@app/data-layer/models/isf-v1';
import { CodeRepository, InvoicesRepository } from '@app/data-layer/repositories';
import { CustomValidator } from '@helpers/custom-validator';
import { FormHelper } from '@helpers/form.helper';
import { UtilsHelper } from '@helpers/utils-helper';
import { ISectionFormField } from '@interfaces/section-form-field';
import { TranslateService } from '@ngx-translate/core';
import { GenericService } from '@services/generic.service';
import { IErrorForm } from '@shared/form-wrapper/interfaces';
import { IsfOcrQuickFixService, IsfStateService } from '@shared/isf-mutual/services';
import { MissingFieldsComponent } from '@shared/missing-fields/components';
import { forkJoin, merge, Observable, of, ReplaySubject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, startWith, switchMap, takeUntil, tap } from 'rxjs/operators';

@Component({
  selector: 'app-isf-ocr-quick-fix-inter-hts-code-details',
  templateUrl: './isf-ocr-quick-fix-inter-hts-code-details.component.html',
  styleUrls: ['./isf-ocr-quick-fix-inter-hts-code-details.component.scss'],
})
export class IsfOcrQuickFixInterHtsCodeDetailsComponent implements OnInit, OnDestroy {
  @ViewChild(MissingFieldsComponent, { static: false }) public missingFieldsModal: MissingFieldsComponent;
  @Input() public manufacturerFields: ISectionFormField[];
  @Input() public invoiceId: number;

  @Output() public dataSaved: EventEmitter<void> = new EventEmitter();

  public isf: IsfModel;
  public shipment: ShipmentModel;
  public formGroup: UntypedFormGroup;
  public displayErrors: Map<string, string> = new Map<string, string>();
  public formErrors: { [name: string]: IErrorForm };

  public manufacturerDisplayFields: ISectionFormField[] = [];
  public enumEntityModelCode: typeof enumEntityModelCode = enumEntityModelCode;
  public enumEntityType: typeof enumEntityType = enumEntityType;
  public initialHtsFormValue: IsfV1EntityHtsModel[] = [];
  public htsFormGroups: UntypedFormGroup[];
  public initializedCountries: CountryModel[];
  public isEditMode: boolean = false;
  public isDesktop: boolean = false;
  public seller: EntityModel;

  private invoice: InvoiceModel;
  private htsNumberCheckSubscription: Subscription = new Subscription();

  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(
    private fh: FormHelper,
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private codeRepository: CodeRepository,
    private genericService: GenericService,
    private invoicesRepository: InvoicesRepository,
    private isfOcrQuickFixService: IsfOcrQuickFixService,
    private translateService: TranslateService,
    private isfStateService: IsfStateService,
  ) {}

  ngOnInit(): void {
    const isf = this.isfOcrQuickFixService.getIsf();
    this.syncIsf(isf);

    this.initializeFormGroups();

    this.initializeCountries();

    this.fh.updateEntityValidation(true, this.formGroup);

    this.genericService.isDesktop$.pipe(takeUntil(this.destroy$)).subscribe((isDesktop) => {
      this.isDesktop = isDesktop;
    });

    this.setFieldsProperties();

    this.populateDisplayErrors(this.formGroup);
    this.createFormErrors();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.htsNumberCheckSubscription.unsubscribe();
  }

  public onBlockEdit(isShowEditBlock: boolean) {
    this.isEditMode = isShowEditBlock;
    this.formGroup.markAllAsTouched();
    this.formGroup.updateValueAndValidity();
  }

  public onSubmit(isNeedCheck: boolean = true): void {
    const manufacturerInvalidFields = this.fh.getInvalidFieldsByCode(this.formGroup.value, enumEntityModelCode.MANUFACTURER, true);
    const htsFormGroupsInvalidFields = this.htsFormGroupsInvalidFields;

    const isFormFieldsInvalid =
      isNeedCheck && (Object.keys(manufacturerInvalidFields).length > 0 || Object.keys(this.htsFormGroupsInvalidFields).length > 0);

    if (isFormFieldsInvalid) {
      this.fh.markAsTouched(this.formGroup);
      this.openMissingFieldsModal({ ...htsFormGroupsInvalidFields, ...manufacturerInvalidFields });
      return;
    }

    this.saveHTS()
      .pipe(
        switchMap(() => this.updateOrCreateManufacturer()),
        takeUntil(this.destroy$),
      )
      .subscribe((isf) => {
        this.isfOcrQuickFixService.setIsf(isf);
        this.isfStateService.currentIsf = isf;
        this.dataSaved.emit();
      });
  }

  public onClearForm(): void {
    this.formGroup.reset();
  }

  public searchCountry() {
    return this.codeRepository.searchCountry.bind(this.codeRepository);
  }

  public addHts() {
    const newHtsFormGroup = this.createEmptyHtsGroup();
    this.htsFormGroups.push(newHtsFormGroup);
    this.htsNumberCheckSubscription.add(
      newHtsFormGroup.valueChanges.subscribe((_) => {
        this.checkHtsNumberControls();
      }),
    );
  }

  public removeHtsItem(index: number): void {
    this.htsFormGroups = this.htsFormGroups.filter((_, i) => i !== index);
  }

  public saveHTS(): Observable<any> {
    return this.isfStateService.saveHTS(this.getFormHTSValue(), this.shipment?.invoices, this.initialHtsFormValue);
  }

  public get manufacturerTitle(): string {
    const currentInvoiceNumber = this.shipment.invoices.findIndex((invoice) => invoice.id === this.invoiceId) + 1;
    const translatedManufacturerText = this.translateService.instant('ENTITY.MANUFACTURER');

    return this.shipment.invoices.length > 1 ? `${translatedManufacturerText} #${currentInvoiceNumber}` : translatedManufacturerText;
  }

  public get isSellerAssignedToOtherManufacturer(): boolean {
    const invoices = this.shipment.invoices;
    const manufacturerIds = invoices.map((invoice) => invoice.manufacturerId);
    const uniqueManufacturerIds = [...new Set(manufacturerIds)];

    return this.seller && this.invoice?.manufacturer?.id !== this.seller.id && uniqueManufacturerIds.includes(this.seller.id);
  }

  private getFormHTSValue(): IsfV1EntityHtsModel[] {
    const entityHts /*: IsfV1EntityHtsModel[]*/ = [];

    this.htsFormGroups.forEach((fg) => {
      if (!!fg.get('number').value || !!fg.get('countryOfOriginId').value) {
        entityHts.push({
          id: fg.get('id').value,
          invoiceId: this.invoiceId,
          number: fg.get('number').value || '',
          countryOfOrigin: fg.get('countryOfOrigin').value,
          countryOfOriginId: fg.get('countryOfOriginId').value,
        });
      }
    });

    return entityHts;
  }

  private createHtsFormGroup(lineItem?: LineModel): UntypedFormGroup {
    return this.fb.group({
      id: this.fb.control(lineItem?.id || 0),
      number: this.fb.control(lineItem?.htsNumber || null, { updateOn: 'blur' }),
      countryOfOrigin: this.fb.control(lineItem?.countryOfOrigin?.code || null),
      countryOfOriginId: this.fb.control(lineItem?.countryOfOrigin?.id || null, CustomValidator.requiredHTSCountryOfOrigin('number')),
      countryName: this.fb.control(lineItem?.countryOfOrigin?.description || null),
    });
  }

  private checkHtsNumberControls() {
    const emptyHts = this.htsFormGroups.filter((fg) => !fg.get('number').value && !fg.get('countryOfOriginId').value);
    const emptyHtsCount = emptyHts.length;
    if (emptyHtsCount === 1) {
      return;
    }

    if (!emptyHtsCount) {
      this.addHts();
      return;
    }

    this.htsFormGroups.forEach((fg, index: number) => {
      if (!fg.get('number').value && !fg.get('countryOfOriginId').value) {
        this.removeHtsItem(index);
      }
    });
  }

  private createEmptyHtsGroup(): UntypedFormGroup {
    return this.fb.group({
      id: this.fb.control(0),
      number: this.fb.control(null, { updateOn: 'blur' }),
      countryOfOrigin: this.fb.control(null),
      countryOfOriginId: this.fb.control(null, CustomValidator.requiredHTSCountryOfOrigin('number')),
      isfEntityId: this.fb.control(this.invoice?.manufacturerId),
    });
  }

  private openMissingFieldsModal(data) {
    this.missingFieldsModal.openCustomModal(data);
  }

  private setFieldsProperties() {
    const manufacturer = this.invoice?.manufacturer;

    const addressFieldMissingPartsErrorText = this.addressFieldMissingPartsErrorText();
    const manufacturerAddressDisplayLine = !!addressFieldMissingPartsErrorText ? '' : this.getAddressLines(manufacturer?.address);
    const nameField = this.manufacturerFields.find((field) => field.name === 'name');

    this.manufacturerDisplayFields.push(
      {
        ...nameField,
        type: 'text',
        label: 'LABELS.NAME',
        name: 'name',
        value: manufacturer?.name,
      },
      {
        type: 'text',
        label: 'LABELS.ADDRESS',
        name: 'address',
        value: manufacturerAddressDisplayLine,
      },
    );

    this.cdr.markForCheck();
  }

  private populateDisplayErrors(fg: UntypedFormGroup) {
    Object.keys(fg.controls).forEach((fieldName) => {
      switch (fieldName) {
        case 'name':
          this.displayErrors.set(fieldName, this.fieldMissingErrorText(fieldName, fg));
          break;
        case 'address':
          this.displayErrors.set(fieldName, this.addressFieldMissingPartsErrorText());
          break;
        default:
          break;
      }
    });
  }

  private createFormErrors(): void {
    this.formErrors = this.fh.createErrorsForIsfFormGroup();
  }

  private fieldMissingErrorText(fieldName: string | string[], fg: UntypedFormGroup): string {
    return !fg.get(fieldName)?.value ? 'LABELS.MISSING' : '';
  }

  private addressFieldMissingPartsErrorText(): string {
    const manufacturerAddressErrors = this.invoice?.manufacturer?.address?.validationMessages || {};
    const missingAddressPartsText = !UtilsHelper.isObjectEmpty(manufacturerAddressErrors) ? 'BACK_ERROR.MISSING_ADDRESS_PARTS' : '';
    return missingAddressPartsText;
  }

  private getAddressLines(data: AddressModel): string {
    if (!data) {
      return '';
    }

    const state = data.state ? data.state.description : null;
    const country = data.country ? data.country.description : null;
    const address = [data?.line1, data?.line2, data?.line3]
      .filter((part) => !!part)
      .join('\n')
      .trim();

    const line1 = [address, data.city]
      .filter((part) => !!part)
      .join(', ')
      .trim();
    const line2 = [country, state]
      .filter((part) => !!part)
      .join(', ')
      .trim();

    return line1 + line2;
  }

  private updateOrCreateManufacturer() {
    const manufacturer: EntityModel = this.formGroup.value;
    const manufacturerId = this.formGroup.get('id').value;

    let newInvoiceData: Partial<InvoiceModel>;

    if (manufacturerId === this.seller?.id) {
      newInvoiceData = {
        ...this.invoice,
        manufacturer: this.seller,
        manufacturerId: this.seller?.id,
      };
    } else {
      newInvoiceData = {
        ...this.invoice,
        manufacturer,
        manufacturerId,
      };
    }

    return of(manufacturer).pipe(
      switchMap((entity) => {
        if (!manufacturerId) {
          entity.addressId = 0;
          entity.address.id = 0;
          entity.entityCode = enumEntityModelCode.MANUFACTURER;
          return this.isfStateService.createEntity(entity);
        } else {
          return this.isfStateService.updateEntity(entity);
        }
      }),
      switchMap((entity) => {
        const invoiceCreateUpdateData = {
          ...newInvoiceData,
          manufacturer: entity,
          manufacturerId: entity.id,
        };

        if (!this.invoiceId) {
          return this.createInvoice(invoiceCreateUpdateData);
        } else {
          return this.updateInvoice(invoiceCreateUpdateData);
        }
      }),
      switchMap((newInvoice) => {
        let invoices: InvoiceModel[] = [];

        if (this.shipment.invoices.length) {
          invoices = this.shipment.invoices.map((invoice) => {
            if (invoice.id === this.invoiceId) {
              return newInvoice;
            }

            return invoice;
          });
        } else {
          invoices = [newInvoice];
        }

        const updatedShipment = this.shipment;
        updatedShipment.invoices = invoices;

        return this.isfStateService.updateShipment(updatedShipment);
      }),
      switchMap((shipment) => {
        return this.isfStateService.updateIsf({
          ...this.isf,
          shipment,
        });
      }),
      switchMap((isf) => this.isfStateService.loadIsf(isf.referenceNumber)),
      tap((isf) => {
        this.syncIsf(isf);
      }),
    );
  }

  private createInvoice(newInvoiceData?: Partial<InvoiceModel>) {
    if (!newInvoiceData) {
      newInvoiceData = { ...this.invoice };
    }

    return this.invoicesRepository.create(this.shipment.id, newInvoiceData);
  }

  private updateInvoice(newInvoiceData?: Partial<InvoiceModel>) {
    if (!newInvoiceData) {
      newInvoiceData = { ...this.invoice };
    }

    return this.invoicesRepository.update(this.invoice.id, newInvoiceData);
  }

  private initializeFormGroups() {
    this.formGroup = this.fh.createEntityModelFormGroup(enumEntityModelCode.MANUFACTURER);

    this.formGroup.patchValue(this.invoice?.manufacturer);

    this.formGroup
      .get('id')
      .valueChanges.pipe(takeUntil(this.destroy$), distinctUntilChanged(), startWith(this.formGroup.get('id').value))
      .subscribe((manufacturerId) => {
        this.applySellerOrManufacturer(manufacturerId);
      });

    merge(this.formGroup.get('name').valueChanges, this.formGroup.get('address').valueChanges)
      .pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe((_) => {
        if (!this.formGroup.pristine && this.seller && this.formGroup.get('id').value === this.seller.id) {
          this.formGroup.get('id').patchValue(this.invoice?.manufacturer?.id || 0);
        }
      });

    if (this.formGroup.invalid) {
      this.formGroup.markAsDirty();
      this.formGroup.markAllAsTouched();
    }

    let isAnyHtsCountryOfOriginEmpty = false;

    if (!this.invoice?.lines?.length) {
      this.htsFormGroups = [this.createHtsFormGroup()];
    } else {
      this.htsFormGroups = this.invoice.lines.map((hts: LineModel) => {
        if (!hts.countryOfOrigin) {
          isAnyHtsCountryOfOriginEmpty = true;
        }
        return this.createHtsFormGroup(hts);
      });
    }

    if (isAnyHtsCountryOfOriginEmpty) {
      this.htsFormGroups.forEach((fg) => {
        if (!fg.get('countryOfOrigin').value) {
          fg.markAllAsTouched();
          fg.updateValueAndValidity();
        }
      });
    }

    this.checkHtsNumberControls();

    this.htsFormGroups.forEach((fg) => {
      this.htsNumberCheckSubscription.add(
        fg.valueChanges.subscribe((_) => {
          this.checkHtsNumberControls();
        }),
      );
    });

    this.initialHtsFormValue = [...this.getFormHTSValue()];
  }

  private initializeCountries() {
    this.htsNumberCheckSubscription.add(
      forkJoin(
        this.htsFormGroups
          .map((control) => control.get('countryOfOrigin'))
          .filter((country) => country.value)
          .map((country) => this.codeRepository.getCountry(country.value)),
      ).subscribe((countries) => {
        this.initializedCountries = [...countries];
        this.cdr.detectChanges();
      }),
    );
  }

  private applySellerOrManufacturer(id: number) {
    if (this.seller && id === this.seller.id) {
      this.formGroup.markAsPristine();
      this.fh.patchIsfEntity(this.formGroup, this.seller);
    } else {
      this.fh.patchIsfEntity(this.formGroup, this.invoice?.manufacturer);
    }
  }

  private syncIsf(isf: IsfModel) {
    this.isf = isf;
    this.shipment = isf.shipment;
    this.invoice = this.isf.shipment.invoices.find((invoice) => invoice.id === this.invoiceId);
    this.seller = this.isf?.shipment?.seller;
  }

  private get htsFormGroupsInvalidFields() {
    let htsInvalidFields = {};

    this.htsFormGroups.forEach((fg) => {
      let invalidFields = this.fh.validateAllFields(fg);
      const htsNumberKey = 'htsNumber';

      if (Object.keys(invalidFields).length === 0) {
        // check for existing code, do not take into account last element
        if (
          this.htsFormGroups.some((item) => {
            const sliceIndex = this.htsFormGroups.length > 0 ? -1 : this.htsFormGroups.length;

            this.htsFormGroups.slice(0, sliceIndex).some((htsFg) => !htsFg.get('number').value);
          })
        ) {
          invalidFields = { [htsNumberKey]: htsNumberKey };
        }
        if (
          this.htsFormGroups.some((item) => {
            const sliceIndex = this.htsFormGroups.length > 0 ? -1 : this.htsFormGroups.length;

            this.htsFormGroups.slice(0, sliceIndex).some((htsFg) => !htsFg.get('countryOfOriginId').value);
          })
        ) {
          invalidFields = { ...invalidFields, countryOfOriginId: 'countryOfOriginId' };
        }
      }

      // introduce hts code validation
      if (invalidFields.hasOwnProperty('number')) {
        delete invalidFields['number'];
        invalidFields[htsNumberKey] = htsNumberKey;
      }

      htsInvalidFields = { ...htsInvalidFields, ...invalidFields };
    });

    return htsInvalidFields;
  }
}
